<script setup lang="ts">
import { useRafFn, useTimeoutFn } from '@vueuse/core'
import { ExperimentCookieName, ExperimentName, ExperimentValue } from '@core/types'

const links = useLinks()
const { isMobile } = useDevice()
// ─── Media references ─────────────────────────────────────────────────────────
// Board images live on the media CDN (media.platego.io); rendered via
// <NuxtImg provider="cloudflare" preset="gameservice">.
const img = useImage()
const { mediaVideoUrl } = useGameAssets()
const experiment = useExperiment({ cookieName: ExperimentCookieName.LANDING_PAGE, experimentName: ExperimentName.LANDING_PAGE })
const { clock } = useClock()

// Every cut and board exists in both orientations, so phones get the real 9:16 file instead of a
// CDN crop. `isMobile` is an SSR-resolved boolean (not a ref), so the choice is stable per request.
// A file re-uploaded under the same name needs a `?v=` query on its path: Cloudflare keys its media
// and image transform caches on the full source URL.
const orientation = isMobile ? 'portrait' : 'landscape'
const device = isMobile ? 'mobile' : 'desktop'
const SPOOKY_SPINS_DIR = '/landing_pages/dk/spilnu/spooky-spins'
const VARIANT_MEDIA = {
    [ExperimentValue.A]: {
        video: `/landing_pages/dk/starlight-${orientation}.mp4`,
        poster: `/landing_pages/dk/starlight-${orientation}-poster.jpg`,
        board: `/landing_pages/dk/spilnu/3/hero-${device}-longer.jpg`,
    },
    [ExperimentValue.B]: {
        video: `${SPOOKY_SPINS_DIR}/spooky-spins-${device}.mp4`,
        poster: `${SPOOKY_SPINS_DIR}/spooky-spins-${device}-poster.jpg`,
        board: `${SPOOKY_SPINS_DIR}/spooky-spins-${device}-loader.jpg`,
    },
}

// The Spooky Spins campaign runs 29 Oct 08:00 to 8 Nov 23:59 (CET), and B's video and board run
// with it, so the hero never advertises the wheel before it opens or after it closes: outside that
// window B shows the Game Sucker cut like A. The server clock decides, so SSR and hydration agree.
const dayjs = useDayjs()
const now = dayjs(clock.value)
const campaignLive = !now.isBefore('2026-10-29T08:00:00+01:00') && !now.isAfter('2026-11-08T23:59:59+01:00')
const variant = campaignLive && experiment.value === ExperimentValue.B ? ExperimentValue.B : ExperimentValue.A
const { video, poster, board: BOARD_IMAGE } = VARIANT_MEDIA[variant]

const VIDEO_SRC = mediaVideoUrl(video, isMobile ? 480 : 1280)
const VIDEO_POSTER = img(poster, { width: isMobile ? 480 : 1280, quality: 70 }, { provider: 'cloudflare' })

const pageLoaded = usePageLoaded()

const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')
const { state: autoplayState, awaitingTap, play: playVideo } = useDeferredVideoAutoplay(videoEl, { reportAs: 'landing-hero', respectReducedMotion: true })

// ─── Phase state (drives template classes; no DOM mutation) ───────────────────
const animReady = ref(false) // cosmetic entrance stagger on the offer lockup
const veilFaded = ref(false) // phase-1 board fades out to reveal video
const videoRevealed = ref(false) // video wrap fades 0 → 1
const dimIn = ref(false) // dark bridge fades in before the board fades out
const dimOut = ref(false) // dark bridge fades away once video has resolved
const lifted = ref(false) // offer slides down into the lower third
const { observeCta, trackCtaClick } = useLandingHeroTracking(videoEl, lifted, autoplayState)
const barHidden = ref(false) // loading bar fades out at the transition
const barWidth = ref(0) // 0 → 100, RAF-driven

// ─── Timing (ms) ──────────────────────────────────────────────────────────────
const START_DELAY = 200
const BAR_DURATION = 4140 // continuous eased fill
const HOLD_MS = 4450 // total phase-1 hold (bar fill + a short beat)
const DIM_LEAD = 380 // dim bridge starts this long before the hold ends
const VEIL_FADE = 1300 // must match the board fade-out transition duration
const SLIDE_DELAY = 800 // extra beat after the hold before the offer slides down

useHead({
    meta: [{ name: 'theme-color', content: '#0d1a2b' }],
    style: [
        {
            innerHTML:
                ':root { --nav-bg-color: transparent; --landing-page-bg-override: #0d1a2b; --sticky-terms-bg-override: #0d1a2b; --sticky-terms-color-override: rgba(255,255,255,0.6); }',
        },
    ],
})

// Cubic ease-in-out (smoothstep): smooth S-curve so the bar accelerates in and
// decelerates out instead of snapping linearly.
function easeInOut(t: number): number {
    return t * t * (3 - 2 * t)
}

let barStart = 0
const { pause: pauseBar, resume: resumeBar } = useRafFn(
    ({ timestamp }) => {
        if (!barStart) barStart = timestamp

        const elapsed = timestamp - barStart
        const t = Math.min(elapsed / BAR_DURATION, 1)
        barWidth.value = easeInOut(t) * 100

        // Start the dim bridge a beat before the hold ends, so the board never
        // pops straight to the video — bright board → dimmed board → video.
        if (elapsed >= HOLD_MS - DIM_LEAD) dimIn.value = true

        if (t >= 1) pauseBar()
    },
    { immediate: false },
)

const { authOpen } = useLandingPageAuth()
const introSkipped = ref(false)

function skipIntro() {
    introSkipped.value = true
    pauseBar()
    barHidden.value = true
    veilFaded.value = true
    videoRevealed.value = true
    dimIn.value = false
    dimOut.value = true
    lifted.value = true
}

watch(authOpen, (open) => {
    if (open) skipIntro()
}, { immediate: true })

onMounted(() => {
    // All timers are scheduled here, inside the component's effect scope, so
    // useTimeoutFn / useRafFn auto-clean on unmount (e.g. mid-animation route
    // change). Delays are absolute, relative to mount.

    // Kick off the cosmetic entrance stagger and the loading-bar fill.
    useTimeoutFn(() => {
        animReady.value = true

        if (!introSkipped.value) resumeBar()
    }, START_DELAY)

    // Reveal: board fades out, video fades in, loading bar hides.
    useTimeoutFn(() => {
        barHidden.value = true
        veilFaded.value = true
        videoRevealed.value = true
    }, START_DELAY + HOLD_MS)

    // Fade the dim bridge away once the board fade-out has mostly resolved.
    useTimeoutFn(
        () => {
            dimIn.value = false
            dimOut.value = true
        },
        START_DELAY + HOLD_MS + VEIL_FADE * 0.6,
    )

    // Slide the offer down into the lower third, held a beat past the reveal.
    useTimeoutFn(
        () => {
            lifted.value = true
        },
        START_DELAY + HOLD_MS + SLIDE_DELAY,
    )
})
</script>

<template>
    <section class="lp-hero" aria-label="Velkomsttilbud" data-lp-section="hero">
        <!-- LAYER 1: looping video background -->
        <div
            class="lp-hero__video"
            :class="{ 'lp-hero__video--revealed': videoRevealed }"
            aria-hidden="true"
        >
            <video
                v-if="pageLoaded"
                ref="videoEl"
                loop
                muted
                playsinline
                disableRemotePlayback
                decoding="async"
                preload="none"
                :poster="VIDEO_POSTER"
                :src="VIDEO_SRC"
            />
        </div>

        <VideoPlayButton v-if="lifted && awaitingTap && !authOpen" @click="playVideo" />

        <!-- LAYER 2: persistent legibility scrim (always present) -->
        <div class="lp-hero__scrim" aria-hidden="true" />

        <!-- LAYER 3: phase-1 board — fades out to reveal the video -->
        <div
            class="lp-hero__veil"
            :class="{ 'lp-hero__veil--faded': veilFaded }"
            aria-hidden="true"
        >
            <!-- The board is the LCP image; NuxtImg preloads the same srcset it renders, which a hand-written link on the bare path missed. -->
            <NuxtImg
                class="lp-hero__veil-img"
                provider="cloudflare"
                preset="gameservice"
                :src="BOARD_IMAGE"
                sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw"
                :quality="75"
                :preload="{ fetchPriority: 'high' }"
                fetchpriority="high"
                decoding="async"
                alt=""
            />
            <div class="lp-hero__veil-scrim" />
        </div>

        <!-- LAYER 4: dim bridge — eases the bright board → dark video jump -->
        <div
            class="lp-hero__dim"
            :class="{ 'lp-hero__dim--in': dimIn, 'lp-hero__dim--out': dimOut }"
            aria-hidden="true"
        />

        <!-- LAYER 10: offer lockup — always on top, always visible -->
        <div class="lp-hero__content">
            <div
                class="lp-offer"
                :class="{
                    'lp-offer--lifted': lifted,
                    'lp-offer--anim': animReady,
                }"
            >
                <p class="lp-offer__eyebrow">
                    Tilbud til nye kunder
                </p>

                <h1 class="lp-offer__headline">
                    Vi fordobler
                    <span class="lp-offer__headline-accent">første indbetaling</span>
                </h1>

                <p class="lp-offer__subline">
                    <strong>100% bonus</strong> op til <strong>1.000 kr.</strong>
                </p>

                <NuxtLink
                    :ref="observeCta"
                    v-bind="links.signup.props()"
                    class="lp-offer__cta"
                    data-test="header-button-signup"
                    @click="trackCtaClick('hero')"
                >
                    Fortsæt
                </NuxtLink>

                <div class="lp-offer__trust">
                    <span class="lp-offer__trust-item">
                        <span
                            class="lp-offer__stars"
                            aria-label="4,5 ud af 5 stjerner"
                            role="img"
                        ><span class="lp-offer__stars-fill" aria-hidden="true">★★★★★</span></span>
                        <span>Fremragende på Trustpilot</span>
                    </span>
                    <span class="lp-offer__trust-sep" aria-hidden="true" />
                    <span class="lp-offer__trust-item">
                        <span aria-hidden="true">⚡</span>
                        <span>Hurtige udbetalinger</span>
                    </span>
                </div>

                <p class="lp-offer__tc">
                    Vilkår gælder — se detaljer nedenfor
                </p>

                <!-- Loading bar (phase 1 only) -->
                <div
                    class="lp-bar"
                    :class="{ 'lp-bar--hidden': barHidden }"
                    aria-hidden="true"
                >
                    <div class="lp-bar__label">
                        Indlæser spil …
                    </div>
                    <div class="lp-bar__track">
                        <div
                            class="lp-bar__fill"
                            :style="{ width: `${barWidth}%` }"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
// ─── Color tokens (from the UX mockup) ────────────────────────────────────────
$navy: #0d1a2b;
$yellow: #ffd400;
$orange-1: #ff7a00;
$easing-out: cubic-bezier(0.22, 1, 0.36, 1);

// ─── Hero wrapper ─────────────────────────────────────────────────────────────
.lp-hero {
    position: relative;
    width: 100%;
    min-height: 100dvh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: $navy;
    color: #fff;

    // Soft shadow rising up from the hero's bottom edge — reads as a dropshadow
    // cast upward, easing the seam where the header meets the terms band below
    // instead of a hard colour break. Sits above the media layers (z 1–4) but
    // below the sticky terms band (z-index 6, which overlaps the hero's bottom)
    // and the offer lockup (z-index 10), so neither gets darkened.
    &::after {
        content: '';
        position: absolute;
        inset: auto 0 0 0;
        height: 120px;
        z-index: 5;
        pointer-events: none;
        background: linear-gradient(to top, rgba(6, 15, 30, 0.6) 0%, transparent 100%);
    }
}

// ─── Layer 1: video background ────────────────────────────────────────────────
// Starts at opacity 0 (hidden behind the board) and fades in at the reveal.
.lp-hero__video {
    position: absolute;
    inset: 0;
    z-index: 1;
    opacity: 0;
    will-change: opacity;
    background-color: $navy;

    &--revealed {
        opacity: 1;
        transition: opacity 1.3s $easing-out;
    }

    video {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

// ─── Layer 2: persistent legibility scrim ─────────────────────────────────────
.lp-hero__scrim {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background:
        radial-gradient(ellipse 75% 40% at 50% 90%, rgba(6, 15, 30, 0.7) 0%, rgba(6, 15, 30, 0.3) 55%, transparent 80%),
        linear-gradient(to bottom, rgba(6, 15, 30, 0.3) 0%, transparent 18%);
}

// ─── Layer 3: phase-1 board ───────────────────────────────────────────────────
.lp-hero__veil {
    position: absolute;
    inset: 0;
    z-index: 3;
    pointer-events: none;
    opacity: 1;
    overflow: hidden;
    will-change: opacity;

    &--faded {
        opacity: 0;
        transition: opacity 1.3s $easing-out;
    }
}

.lp-hero__veil-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: translateX(-10px) scale(1.1);
    transform-origin: center;

    @include tablet(md) {
        transform: translate(-20px, -10px);
    }
}

.lp-hero__veil-scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
        radial-gradient(
            ellipse 90% 45% at 50% 108%,
            rgba(5, 15, 35, 0.78) 0%,
            rgba(5, 15, 35, 0.35) 45%,
            transparent 68%
        ),
        linear-gradient(to bottom, rgba(5, 15, 35, 0.3) 0%, transparent 20%);
}

// ─── Layer 4: dim bridge ──────────────────────────────────────────────────────
.lp-hero__dim {
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(5, 15, 35, 0.72);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.7s ease-in;
    will-change: opacity;

    &--in {
        opacity: 1;
    }

    &--out {
        opacity: 0;
        transition: opacity 0.8s 0.3s ease-out;
    }
}

// ─── Layer 10: offer lockup ───────────────────────────────────────────────────
.lp-hero__content {
    position: relative;
    z-index: 10;
    isolation: isolate;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    flex: 1;
    pointer-events: none;
}

.lp-offer {
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0 20px;
    width: 100%;
    max-width: 82vw;

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: transform 0.9s $easing-out;

    // Slides down as a transform, so the lift is not a layout shift.
    &--lifted {
        max-width: 90vw;
        transform: translate(-50%, -50%) translateY(25vh);
        transform: translate(-50%, -50%) translateY(25dvh);
    }

    @include tablet(md) {
        max-width: min(50vw, 760px);
        top: 55%;

        &--lifted {
            transform: translate(-50%, -50%) translateY(20vh);
            transform: translate(-50%, -50%) translateY(20dvh);
        }
    }

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: rgba(10, 20, 10, 0.55);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 999px;
        padding: 4px 10px;
        margin-bottom: 9px;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(4px);

        &::before {
            content: '';
            display: inline-block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: $orange-1;
            box-shadow: 0 0 6px 2px rgba(255, 122, 0, 0.7);
            animation: lp-pulse-dot 2s ease-in-out infinite;
        }
    }

    &__headline {
        margin: 0 0 4px;
        // Sign width scales with viewport height (cover-cropped) on portrait phones; cap via svh so the headline keeps fitting the wooden sign.
        font-size: min(clamp(38px, 11vw, 64px), 5.3svh);
        font-weight: 900;
        line-height: 0.88;
        letter-spacing: -0.04em;
        color: $yellow;
        text-transform: uppercase;
        white-space: nowrap;
        text-shadow:
            0 1px 0 rgba(0, 0, 0, 0.9),
            0 2px 0 rgba(0, 0, 0, 0.6),
            0 4px 20px rgba(0, 0, 0, 0.7),
            0 8px 40px rgba(0, 0, 0, 0.5);

        @include tablet(md) {
            // The sign is part of the cover-cropped board photo (aspect 1.5, sign = 37.5% of
            // image width), so its rendered width is 0.375 * max(100vw, 150svh). 4.1vw/6.2svh
            // keeps the widest headline line at <= ~95% of the sign on both cover axes.
            font-size: clamp(48px, max(4.1vw, 6.2svh), 74px);
            margin-bottom: 5px;
        }
    }

    &__headline-accent {
        display: block;
        margin-top: 2px;
        color: #fff;
        font-size: 0.78em;
        letter-spacing: 0.01em;
        white-space: nowrap;
    }

    &__subline {
        margin: 0 0 12px;
        font-size: clamp(15px, 4.2vw, 21px);
        font-weight: 700;
        color: rgba(255, 255, 255, 0.97);
        letter-spacing: 0.01em;
        text-shadow:
            0 1px 0 rgba(0, 0, 0, 0.8),
            0 2px 12px rgba(0, 0, 0, 0.65),
            0 4px 24px rgba(0, 0, 0, 0.5);

        strong {
            color: #fff;
        }

        @include tablet(md) {
            font-size: clamp(17px, 2vw, 22px);
        }
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        min-width: 230px;
        padding: 18px 100px;
        border: none;
        border-radius: 999px;
        background: linear-gradient(180deg, #ff8a1a 0%, #ff7a00 100%);
        color: #fff;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: 0.01em;
        text-decoration: none;
        text-shadow: 0 2px 0 rgba(0, 0, 0, 0.3);
        white-space: nowrap;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        box-shadow:
            0 4px 20px rgba(255, 122, 0, 0.55),
            0 1px 0 rgba(255, 255, 255, 0.15) inset;
        transition:
            transform 0.15s,
            box-shadow 0.15s;

        // Sheen sweep
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -80%;
            width: 60%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
            transform: skewX(-20deg);
            animation: lp-shimmer 3.5s ease-in-out 1.5s infinite;
        }

        &:hover {
            transform: translateY(-1px);
            box-shadow:
                0 6px 28px rgba(255, 122, 0, 0.72),
                0 1px 0 rgba(255, 255, 255, 0.2) inset;
        }

        &:active {
            transform: scale(0.97) translateY(1px);
        }
    }

    &__trust {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 11px;
        font-size: 11px;
        color: rgba(255, 255, 255, 0.55);
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
    }

    &__trust-item {
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
    }

    &__trust-sep {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.25);
        flex-shrink: 0;
    }

    &__stars {
        position: relative;
        display: inline-block;
        color: rgba(255, 255, 255, 0.25); // empty (unfilled) star track
        letter-spacing: -1px;

        // Five-star track rendered via ::before so the filled overlay can sit on
        // top, clipped to 90% width (4,5 of 5 stars).
        &::before {
            content: '★★★★★';
        }
    }

    &__stars-fill {
        position: absolute;
        top: 0;
        left: 0;
        width: 90%;
        overflow: hidden;
        white-space: nowrap;
        color: $yellow;
    }

    &__tc {
        margin-top: 9px;
        font-size: 10px;
        letter-spacing: 0.02em;
        color: rgba(255, 255, 255, 0.38);
    }
}

// ─── Phase-2 entrance stagger (cosmetic) ──────────────────────────────────────
.lp-offer--anim {
    .lp-offer__eyebrow {
        animation: lp-slide-in 0.5s 0.1s $easing-out both;
    }
    .lp-offer__headline {
        animation: lp-slide-in 0.6s 0.25s $easing-out both;
    }
    .lp-offer__subline {
        animation: lp-slide-in 0.6s 0.42s $easing-out both;
    }
    .lp-offer__cta {
        animation: lp-slide-in 0.6s 0.56s $easing-out both;
    }
    .lp-offer__trust {
        animation: lp-fade-in 0.6s 0.72s both;
    }
    .lp-offer__tc {
        animation: lp-fade-in 0.5s 0.85s both;
    }
}

// ─── Loading bar ──────────────────────────────────────────────────────────────
.lp-bar {
    width: 100%;
    max-width: 310px;
    margin: 20px auto 0;
    opacity: 1;
    transition: opacity 0.5s ease-out;

    &--hidden {
        opacity: 0;
        pointer-events: none;
    }

    &__label {
        margin-bottom: 6px;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        text-align: center;
        color: #fff;
    }

    &__track {
        width: 100%;
        height: 16px;
        background: rgba(0, 0, 0, 0.45);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 999px;
        overflow: hidden;
    }

    &__fill {
        height: 100%;
        width: 0;
        border-radius: 999px;
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, transparent 40%, transparent 100%),
            linear-gradient(90deg, #0a6cf0 0%, #2f9bff 45%, #66c4ff 100%);
        box-shadow:
            0 0 14px 4px rgba(60, 150, 255, 0.75),
            0 0 4px 1px rgba(150, 210, 255, 0.5);
        will-change: width;
    }
}

// ─── Keyframes ────────────────────────────────────────────────────────────────
@keyframes lp-pulse-dot {
    0%,
    100% {
        box-shadow: 0 0 6px 2px rgba(255, 122, 0, 0.7);
    }
    50% {
        box-shadow: 0 0 10px 4px rgba(255, 122, 0, 0.9);
    }
}

@keyframes lp-shimmer {
    0% {
        transform: translateX(0) skewX(-20deg);
        opacity: 0;
    }
    10% {
        opacity: 1;
    }
    60% {
        transform: translateX(350%) skewX(-20deg);
        opacity: 0;
    }
    100% {
        transform: translateX(350%) skewX(-20deg);
        opacity: 0;
    }
}

@keyframes lp-slide-in {
    from {
        transform: translateY(10px);
        opacity: 0.4;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes lp-fade-in {
    from {
        opacity: 0.2;
    }
    to {
        opacity: 1;
    }
}

// ─── StickyTerms full-width band override ─────────────────────────────────────
// <StickyTerms> is a sibling of this component in index.vue; scoped styles can't
// reach it, so target it via :global() scoped to .landing-page. Bust the
// .container / .sticky-terms--landing-page width caps so the navy band bleeds
// edge-to-edge, then re-apply the 1024px column cap on the legal text.
:global(.landing-page .offer-terms--sticky) {
    background: $navy
        linear-gradient(180deg, rgba(13, 26, 43, 0) 0%, rgba(13, 26, 43, 0.85) 55%, rgba(13, 26, 43, 0.96) 100%);
    max-width: none;
}

:global(.landing-page .offer-terms--sticky.container) {
    max-width: none;
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    margin-left: 0;
    margin-right: 0;
}

:global(.landing-page .offer-terms--sticky .offer-terms__text) {
    max-width: 1024px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 15px;
    padding-right: 15px;
}

// :global() must wrap the whole selector: `:global(.a) .b` compiles to a bare
// `.a` rule in scoped blocks, which hid the entire landing page.
:global(.landing-page--auth-open .lp-hero__content) {
    display: none;
}

:global(.landing-page--auth-open .lp-hero__scrim) {
    display: none;

    @include tablet {
        display: block;
    }
}
</style>
